import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { analyticsAPI } from '../services/api';
import Loader from '../components/Loader';
import './Dashboard.css';

interface DashboardData {
  overview: {
    totalAccounts: number;
    connectedAccounts: number;
    totalGroups: number;
    totalMembers: number;
    avgMembersPerGroup: number;
    recentGroups: number;
  };
  totalOverview?: {
    totalAccounts: number;
    totalGroups: number;
    totalMembers: number;
    avgMembersPerGroup: number;
    recentGroups: number;
  };
  groupsByAccount: any[];
  topGroups: any[];
  groupsBySize: any[];
  accountStatus: any[];
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await analyticsAPI.getDashboard();
      setData(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !data) {
    return <Loader fullScreen text="Loading dashboard..." />;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  if (!data) {
    return null;
  }

  const totalData = data.totalOverview || data.overview;

  return (
    <div className="dashboard">
      <h1 className="text-3xl font-bold text-gray-800 mb-6">Dashboard Overview</h1>

      {/* Total Statistics */}
      <div style={{ marginBottom: '2rem' }}>
        <h2 className="text-xl font-semibold text-gray-700 mb-4" style={{ marginBottom: '1rem' }}>
          Total Statistics
        </h2>
        <div className="stats-grid">
          <div className="stat-card">
            <h3>Total Accounts</h3>
            <div className="value">{totalData.totalAccounts}</div>
            <div className="stat-subtitle">
              Including archived
            </div>
          </div>
          
          <div className="stat-card">
            <h3>Total Groups</h3>
            <div className="value">{totalData.totalGroups}</div>
            <div className="stat-subtitle">
              {totalData.recentGroups} added this week
            </div>
          </div>
          
          <div className="stat-card">
            <h3>Total Members</h3>
            <div className="value">{totalData.totalMembers.toLocaleString()}</div>
            <div className="stat-subtitle">
              Across all groups (with overlap)
            </div>
          </div>
          
          <div className="stat-card">
            <h3>Avg Group Size</h3>
            <div className="value">{totalData.avgMembersPerGroup}</div>
            <div className="stat-subtitle">
              Members per group
            </div>
          </div>
        </div>
      </div>

      <div className="dashboard-grid">
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h2 style={{ margin: 0 }}>Groups by Account</h2>
            {data.groupsByAccount.length > 0 && (
              <button
                onClick={() => navigate('/groups')}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#0e4842',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#0a3a35'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#0e4842'}
              >
                View All Groups
              </button>
            )}
          </div>
          {data.groupsByAccount.length > 0 ? (
            <div className="groups-by-account">
              {data.groupsByAccount.slice(0, 5).map((item, index) => (
                <div key={index} className="account-item">
                  <div className="account-info">
                    <strong>{item.accountName}</strong>
                    <span className="account-phone">{item.phoneNumber}</span>
                  </div>
                  <div className="account-stats">
                    <span>{item.groupsCount} groups</span>
                    <span>{item.membersCount} members</span>
                  </div>
                </div>
              ))}
              {data.groupsByAccount.length > 5 && (
                <p style={{ textAlign: 'center', color: '#666', marginTop: '0.5rem', fontSize: '0.9rem' }}>
                  Showing 5 of {data.groupsByAccount.length} accounts
                </p>
              )}
            </div>
          ) : (
            <p>No groups data available</p>
          )}
        </div>

        <div className="card">
          <h2>Top Groups by Size</h2>
          {data.topGroups.length > 0 ? (
            <div className="top-groups">
              {data.topGroups.map((group, index) => (
                <div key={index} className="group-item">
                  <div className="group-name">{group.name}</div>
                  <div className="group-members">
                    {group.participantsCount} members
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p>No groups data available</p>
          )}
        </div>
      </div>

      <div className="card">
        <h2>Account Status Distribution</h2>
        <div className="status-distribution">
          {data.accountStatus.map((status, index) => (
            <div key={index} className="status-item">
              <span className="status-label">{status._id}</span>
              <span className="status-count">{status.count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;

